import { SHADING_2026_ENABLED } from '../featureFlags';

// ~11 km x 9 km around the address: enough to pan around a few neighborhoods
// while keeping one request's GeoEnrichment cost and payload small.
const FRAME_HALF_LON = 0.05;
const FRAME_HALF_LAT = 0.04;

const finite = n => typeof n === 'number' && Number.isFinite(n);

/** lon/lat bbox around the report address, or null without usable coordinates. */
export function shadingExtentFromAddress(addressInfo) {
  const lon = Number(addressInfo?.address_longitude);
  const lat = Number(addressInfo?.address_latitude);
  if (!finite(lon) || !finite(lat)) return null;
  return {
    xmin: lon - FRAME_HALF_LON,
    ymin: lat - FRAME_HALF_LAT,
    xmax: lon + FRAME_HALF_LON,
    ymax: lat + FRAME_HALF_LAT,
  };
}

/**
 * Make a layer/legend title state the data year actually shown.
 * "2024 Median Household Income" -> "2026 Median Household Income";
 * "2024-2029 Population Growth" -> "2026-2031 Population Growth" (range shifts
 * by the same offset); a title with no year gets " (2026)" appended.
 */
export function retitleForVintage(text, vintage) {
  if (typeof text !== 'string' || !text || !vintage) return text;
  const target = Number(vintage);
  if (!finite(target)) return text;

  // Any non-digit counts as a boundary (\b would not split "Population_2024"
  // because "_" is a word character).
  const range = text.match(/(^|[^0-9])((?:19|20)\d{2})\s*[-–]\s*((?:19|20)\d{2})(?![0-9])/);
  if (range) {
    const delta = target - Number(range[2]);
    return text.replace(range[0], `${range[1]}${target}-${Number(range[3]) + delta}`);
  }
  const single = text.match(/(^|[^0-9])((?:19|20)\d{2})(?![0-9])/);
  if (single) return text.replace(single[0], `${single[1]}${target}`);
  return `${text} (${target})`;
}

// Backend reasons meaning "this block isn't eligible for 2026 shading" (the
// viewer draws the layer as authored). Anything else that stops us getting
// 2026 polygons is a FAILURE, and viewers must not substitute the 2024 layer.
const NOT_APPLICABLE_REASONS = new Set([
  'disabled',
  'layer-not-routable',
  'not-block-group-layer',
  'field-not-mapped',
  'invalid-extent',
]);

export const SHADING_UNAVAILABLE_MESSAGE =
  '2026 data is temporarily unavailable for this map. Please try again shortly.';

/**
 * Ask the backend for 2026 GeoEnrichment polygons for a saved report's map
 * block. Resolves one of:
 *   { status: 'shaded', data }          - draw these polygons
 *   { status: 'not-applicable', reason } - flag off / block not eligible: draw the layer as authored
 *   { status: 'failed', reason }        - eligible but GeoEnrichment could not serve it: do NOT draw the 2024 layer
 * Works without a signed-in token (shared/public views); the token is sent when present.
 */
export async function requestReportShading({
  selectedBlock,
  addressInfo,
  getAccessToken,
  fetchImpl = (...args) => fetch(...args),
  baseUrl = process.env.REACT_APP_NODE_URL,
}) {
  try {
    if (!SHADING_2026_ENABLED) return { status: 'not-applicable', reason: 'flag-off' };
    const outfield = selectedBlock?.outfields?.[0];
    const extent = shadingExtentFromAddress(addressInfo);
    if (!selectedBlock?.serviceURL || !outfield || !extent) {
      return { status: 'not-applicable', reason: 'incomplete-block' };
    }

    const token = typeof getAccessToken === 'function' ? getAccessToken() : null;
    const response = await fetchImpl(`${baseUrl}/report-shading`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ serviceURL: selectedBlock.serviceURL, outfield, extent }),
    });
    if (!response.ok) return { status: 'failed', reason: `http-${response.status}` };

    const data = await response.json();
    if (data?.supported && Array.isArray(data.features) && data.features.length) {
      return { status: 'shaded', data };
    }
    const reason = data?.reason || 'no-data';
    return NOT_APPLICABLE_REASONS.has(reason)
      ? { status: 'not-applicable', reason }
      : { status: 'failed', reason };
  } catch (error) {
    console.warn('[reportShading] request failed:', error?.message || error);
    return { status: 'failed', reason: 'network' };
  }
}

/** Polygons when the block was shaded from 2026 data, otherwise null. */
export async function fetchReportShading(args) {
  const result = await requestReportShading(args);
  return result.status === 'shaded' ? result.data : null;
}

/**
 * Build a client-side FeatureLayer from the returned polygons. The layer's
 * field is named exactly like the block's stored outfield so the viewers' own
 * renderer/legend/popup code runs on it unchanged.
 *
 * @param {Object} deps - { FeatureLayer, Graphic } from @arcgis/core (injected for tests).
 */
export function buildShadedFeatureLayer({ FeatureLayer, Graphic }, data, selectedBlock, { title, opacity = 0.6 } = {}) {
  const field = selectedBlock.outfields[0];
  const spatialReference = data.spatialReference?.wkid
    ? { wkid: data.spatialReference.wkid }
    : { wkid: 102100 };

  const graphics = data.features.map(
    (feature, index) =>
      new Graphic({
        geometry: { type: 'polygon', rings: feature.rings, spatialReference },
        attributes: { OBJECTID: index + 1, GEOID: feature.id, [field]: feature.value },
      }),
  );

  const layer = new FeatureLayer({
    source: graphics,
    objectIdField: 'OBJECTID',
    geometryType: 'polygon',
    spatialReference,
    fields: [
      { name: 'OBJECTID', type: 'oid' },
      { name: 'GEOID', type: 'string' },
      { name: field, type: data.categorical ? 'string' : 'double' },
    ],
    title,
    popupTemplate: {
      content: `{${field}}`,
      fieldInfos: [{ fieldName: field, format: { digitSeparator: true, places: 0 } }],
    },
  });
  layer.opacity = opacity;
  layer.minScale = 0;
  layer.maxScale = 0;
  return layer;
}
